import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, Key, Palette, Save, Loader2, Upload, Building2, Trash2, ImageIcon } from 'lucide-react';

export default function ConfiguracoesPage() {
  const [pixConfig, setPixConfig] = useState({ tipo_chave_pix: '', chave_pix: '', nome_recebedor_pix: '' });
  const [visualConfig, setVisualConfig] = useState({ nome_escola: '', logo_url: '', cor_primaria: '#007AFF', cor_secundaria: '#FF3B30' });
  const [loading, setLoading] = useState(true);
  const [savingPix, setSavingPix] = useState(false);
  const [savingVisual, setSavingVisual] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const [pix, visual] = await Promise.all([
        api.get('/gestor/config-pix'),
        api.get('/gestor/config-visual')
      ]);
      setPixConfig(pix.data);
      setVisualConfig(visual.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const salvarPix = async () => {
    if (!pixConfig.tipo_chave_pix || !pixConfig.chave_pix) {
      toast.error('Preencha o tipo e o valor da chave PIX');
      return;
    }
    setSavingPix(true);
    try {
      await api.put('/gestor/config-pix', pixConfig);
      toast.success('Chave PIX salva com sucesso!');
    } catch { toast.error('Erro ao salvar'); }
    setSavingPix(false);
  };

  const salvarVisual = async () => {
    setSavingVisual(true);
    try {
      const res = await api.put('/gestor/config-visual', visualConfig);
      setVisualConfig(res.data);
      toast.success('Visual atualizado! Recarregue para ver as mudancas.');
    } catch { toast.error('Erro ao salvar'); }
    setSavingVisual(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Formato invalido. Permitidos: PNG, JPG, JPEG, WEBP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Maximo: 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/gestor/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVisualConfig(p => ({ ...p, logo_url: res.data.logo_url }));
      toast.success('Logo enviada com sucesso!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar logo');
    }
    setUploadingLogo(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLogoRemove = async () => {
    setUploadingLogo(true);
    try {
      await api.delete('/gestor/upload-logo');
      setVisualConfig(p => ({ ...p, logo_url: '' }));
      toast.success('Logo removida');
    } catch { toast.error('Erro ao remover logo'); }
    setUploadingLogo(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48 bg-[#141414]" />
        <Skeleton className="h-64 bg-[#141414]" />
        <Skeleton className="h-64 bg-[#141414]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl" data-testid="configuracoes-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
        <Settings className="w-7 h-7 inline mr-2 text-[var(--cor-primaria,#007AFF)]" />CONFIGURACOES
      </h1>

      <Card className="bg-[#141414] border-[#27272A]" data-testid="config-pix-card">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase flex items-center gap-2">
            <Key className="w-5 h-5 text-green-400" /> Chave PIX para Recebimento
          </CardTitle>
          <CardDescription>Essa chave sera usada para gerar os QR Codes de pagamento dos alunos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo de Chave</Label>
            <Select value={pixConfig.tipo_chave_pix} onValueChange={(v) => setPixConfig(p => ({ ...p, tipo_chave_pix: v }))}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="pix-tipo-select">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#27272A]">
                <SelectItem value="cpf">CPF</SelectItem>
                <SelectItem value="cnpj">CNPJ</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="telefone">Telefone</SelectItem>
                <SelectItem value="aleatoria">Chave Aleatoria</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Valor da Chave</Label>
            <Input
              placeholder="Ex: 123.456.789-00"
              value={pixConfig.chave_pix}
              onChange={(e) => setPixConfig(p => ({ ...p, chave_pix: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A]"
              data-testid="pix-chave-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome do Recebedor (opcional)</Label>
            <Input
              placeholder="Nome que aparecera no PIX"
              value={pixConfig.nome_recebedor_pix}
              onChange={(e) => setPixConfig(p => ({ ...p, nome_recebedor_pix: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A]"
              data-testid="pix-nome-input"
            />
          </div>
          <Button onClick={salvarPix} disabled={savingPix} className="bg-green-600 hover:bg-green-700" data-testid="salvar-pix-btn">
            {savingPix ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Chave PIX
          </Button>
        </CardContent>
      </Card>

      <Separator className="bg-[#27272A]" />

      <Card className="bg-[#141414] border-[#27272A]" data-testid="config-visual-card">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg tracking-tight uppercase flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-400" /> Identidade Visual
          </CardTitle>
          <CardDescription>Personalize a aparencia do sistema para seus alunos e colaboradores.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome da Escola</Label>
            <Input
              placeholder="Ex: Academia Power Fit"
              value={visualConfig.nome_escola}
              onChange={(e) => setVisualConfig(p => ({ ...p, nome_escola: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A]"
              data-testid="visual-nome-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Logo</Label>
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-20 h-20 rounded-lg border border-[#27272A] bg-[#0A0A0A] flex items-center justify-center overflow-hidden shrink-0">
                {visualConfig.logo_url ? (
                  <img
                    src={visualConfig.logo_url}
                    alt="Logo"
                    className="w-full h-full object-contain p-1"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div className={`flex-col items-center justify-center text-zinc-600 ${visualConfig.logo_url ? 'hidden' : 'flex'}`}>
                  <ImageIcon className="w-6 h-6" />
                  <span className="text-[9px] mt-0.5">Sem logo</span>
                </div>
              </div>
              {/* Actions */}
              <div className="flex flex-col gap-2 flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingLogo}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E] gap-2"
                  data-testid="upload-logo-button"
                >
                  {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar logo
                </Button>
                {visualConfig.logo_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={uploadingLogo}
                    onClick={handleLogoRemove}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2 justify-start"
                    data-testid="remove-logo-button"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remover
                  </Button>
                )}
                <p className="text-[10px] text-zinc-600">PNG, JPG ou WEBP. Max 2MB.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cor Primaria</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={visualConfig.cor_primaria}
                  onChange={(e) => setVisualConfig(p => ({ ...p, cor_primaria: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[#27272A]"
                  data-testid="visual-cor-primaria"
                />
                <Input
                  value={visualConfig.cor_primaria}
                  onChange={(e) => setVisualConfig(p => ({ ...p, cor_primaria: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cor Secundaria</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={visualConfig.cor_secundaria}
                  onChange={(e) => setVisualConfig(p => ({ ...p, cor_secundaria: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer bg-transparent border border-[#27272A]"
                  data-testid="visual-cor-secundaria"
                />
                <Input
                  value={visualConfig.cor_secundaria}
                  onChange={(e) => setVisualConfig(p => ({ ...p, cor_secundaria: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm"
                />
              </div>
            </div>
          </div>
          <div className="mt-3 p-3 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Pre-visualizacao</p>
            <div className="flex gap-3 items-center">
              <div className="h-8 w-8 rounded" style={{ backgroundColor: visualConfig.cor_primaria }} />
              <div className="h-8 w-8 rounded" style={{ backgroundColor: visualConfig.cor_secundaria }} />
              <span className="text-sm text-zinc-300">{visualConfig.nome_escola || 'Nome da Escola'}</span>
            </div>
          </div>
          <Button onClick={salvarVisual} disabled={savingVisual} className="bg-purple-600 hover:bg-purple-700" data-testid="salvar-visual-btn">
            {savingVisual ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Visual
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
